export const CHART_BG_LIGHT = '#ffffff';
/** Dark chart pane / platform background (matches `public/charting_library/css/style.css` `.theme-dark`). */
export const CHART_BG_DARK = '#181a20';

/** Neutral grid (TV default dark grids are bluish). */
const GRID_DARK = 'rgba(255, 255, 255, 0.06)';
const GRID_LIGHT = 'rgba(0, 0, 0, 0.06)';
const CROSSHAIR_DARK = 'rgba(255, 255, 255, 0.2)';
const CROSSHAIR_LIGHT = 'rgba(0, 0, 0, 0.2)';

/** Matches app shell theme (body class) — same source used for chart init and live updates. */
export function getChartThemeFromBody() {
  return document.body.classList.contains('light_theme') ? 'light' : 'dark';
}

/**
 * Pane / scale colors that force solid #181a20 (or light white) instead of
 * TradingView's default dark navy/blue pane (#131722 / #1e222d).
 */
export function getChartPaneOverrides(theme = getChartThemeFromBody()) {
  const isLight = theme === 'light';
  const bg = isLight ? CHART_BG_LIGHT : CHART_BG_DARK;
  const txt = isLight ? '#1a1a1a' : '#ffffff';
  const grid = isLight ? GRID_LIGHT : GRID_DARK;
  const crosshair = isLight ? CROSSHAIR_LIGHT : CROSSHAIR_DARK;
  return {
    'paneProperties.background': bg,
    'paneProperties.backgroundType': 'solid',
    'paneProperties.backgroundGradientStartColor': bg,
    'paneProperties.backgroundGradientEndColor': bg,
    'paneProperties.vertGridProperties.color': grid,
    'paneProperties.horzGridProperties.color': grid,
    'paneProperties.crossHairProperties.color': crosshair,
    'scalesProperties.backgroundColor': bg,
    'scalesProperties.textColor': txt,
    'scalesProperties.lineColor': grid,
  };
}

const CHART_THEME_STYLE_ID = 'tv-chart-theme-inline';

/**
 * @param {string} containerSelector - e.g. '#TVChartContainer' or '#TVFuturesChartContainer'
 * @param {string} bgColor
 */
export function injectIframeThemeStyles(containerSelector, bgColor) {
  let iframe;
  let doc;
  try {
    iframe = document.querySelector(`${containerSelector} iframe`);
    if (!iframe) return;
    doc = iframe.contentDocument;
    if (!doc?.head) return;
  } catch {
    /* cross-origin or restricted iframe — cannot inject */
    return;
  }
  let el = doc.getElementById(CHART_THEME_STYLE_ID);
  if (!el) {
    el = doc.createElement('style');
    el.id = CHART_THEME_STYLE_ID;
    doc.head.appendChild(el);
  }
  try {
    el.textContent = `
    @font-face {
      font-family: 'HarmonyOS Sans Regular';
      src: url('/font/HarmonyOS_Sans_Regular.woff') format('woff');
      font-weight: normal;
      font-style: normal;
    }
    body, .chart-page, .chart-container, .tv-text, .pane-legend {
      font-family: 'HarmonyOS Sans Regular', sans-serif !important;
      font-size: 14px !important;
    }
    html, body, .chart-page, .chart-container-border, .chart-container,
    .layout__area--center, .chart-markup-table, .chart-gui-wrapper,
    .chart-container__inner, .chart-widget, .js-rootresizer__contents {
      background: ${bgColor} !important;
      background-color: ${bgColor} !important;
    }
    canvas {
      background-color: transparent !important;
    }
  `;
  } catch {
    /* ignore if DOM becomes unavailable */
  }
}

/** Updates TradingView widget + iframe from `document.body` theme (no remount). */
export function applyChartThemeToWidget(instance, containerSelector) {
  const t = getChartThemeFromBody();
  const bg = t === 'light' ? CHART_BG_LIGHT : CHART_BG_DARK;
  const overrides = getChartPaneOverrides(t);

  // changeTheme restores TV's bluish dark defaults — re-apply our pane colors after.
  const applyOverrides = () => {
    try {
      const chart = instance.chart();
      if (chart) chart.applyOverrides(overrides);
    } catch {
      /* chart not ready */
    }
    injectIframeThemeStyles(containerSelector, bg);
  };

  try {
    const themePromise = instance.changeTheme(t === 'light' ? 'light' : 'dark');
    if (themePromise && typeof themePromise.then === 'function') {
      themePromise.then(() => {
        applyOverrides();
        // Saved layouts / theme settle can overwrite once more — force again.
        setTimeout(applyOverrides, 50);
        setTimeout(applyOverrides, 200);
      }).catch(() => {
        applyOverrides();
      });
    } else {
      applyOverrides();
      setTimeout(applyOverrides, 50);
      setTimeout(applyOverrides, 200);
    }
  } catch {
    applyOverrides();
  }

  requestAnimationFrame(() => {
    injectIframeThemeStyles(containerSelector, bg);
  });
}
